import R from 'ramda';
import { takeLatest, put, call, cancelled } from 'redux-saga/effects';
import { normalize } from 'normalizr';

import {
  company as companySchema,
  companies as companiesSchema,
} from '../schema/company';

import {
  businessInformation as businessInformationSchema,
  businessInformations as businessInformationsSchema,
} from '../schema/businessInformation';

import {
  technicalInformation as technicalInformationSchema,
  technicalInformations as technicalInformationsSchema,
} from '../schema/technicalInformation';

import {
  contract as contractSchema,
  contracts as contractsSchema,
} from '../schema/contract';

import {
  users as usersSchema,
} from '../schema/user';

import {
  budget as budgetSchema,
  budgets as budgetsSchema,
} from '../schema/budget';

import * as utilities from '../reducers/utils';

import {
  REQUEST_COMPANYLIST_START,
  REQUEST_BUSINESSINFO_START,
  REQUEST_BUSINESSINFO_ORDER_START,
  REQUEST_COMPANY_STRUCTURE_START,
  REQUEST_TECHDOCLIST_START,
  REQUEST_TECHDOC_START,
  UPDATE_TECHDOC,
  SAVE_TECHDOC_START,
  CREATE_TECHDOC,
  SET_TECHDOC_TO_DRAFT_START,
  UPDATE_BUSINESSINFO_NOTES_START,
  ADD_COMPANY,
  UPDATE_BUSINESSINFO_STATUS_START,
  SEARCH_COMPANY_START,
  REQUEST_COMPANY_START,
  REMOVE_TECHDOC_START,
  REQUEST_COMPANY_CONTRACTS_START,
  UPDATE_CONTRACT_START,
  CREATE_CONTRACT_START,
  REQUEST_COMPANY_BUDGETS_START,
  CREATE_COMPANY_BUDGET_START,
  UPDATE_COMPANY_BUDGET_START,
  EDIT_COMPANY_START,
} from '../actions';

angular.module('tct.sagas.company', [])
  .factory('companySagaFactory', companySagaFactory);

companySagaFactory.$inject = [
  'actionsFactory',
  'companiesService',
  'businessInfoService',
  'uploadService',
  'mappingService',
  '$rootScope',
  '$state',
];

function companySagaFactory(
  actionsFactory,
  companiesService,
  businessInfoService,
  uploadService,
  mappingService,
  $rootScope,
  $state) {
  const factory = {
    companyFlow,
    companyListFlow,
    businessInfoFlow,
    businessInfoOrderFlow,
    companyStructureFlow,
    techDocsFlow,
    techDocListFlow,
    techDocUpdateFlow,
    techDocSaveFlow,
    createTechDocFlow,
    updateBusinessInfoNotesFlow,
    createCompanyFlow,
    updateBusinessInfoStatusFlow,
    searchCompanyFlow,
    removeTechDocFlow,
    companyContractsFlow,
    companyBudgetsFlow,
  };

  return factory;

  function* getCompanyList(action) {
    try {
      const response = yield call(companiesService.getCompanies, action.paramList);
      const pagination = mappingService.objectKeysToCamel(
        response.data && response.data.data);

      yield put(actionsFactory.reqCompanyListSuccess({
        items: normalize(pagination.items, companiesSchema),
        total: pagination.totalItems,
      }));
      return response;
    } catch (err) {
      yield put(actionsFactory.reqCompanyListError((err.data && err.data.error) || err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return 'done';
  }

  function* getCompanyDetail(action) {
    try {
      const response = yield call(companiesService.getCompany, action.id);
      const company = mappingService.objectKeysToCamel(response.data && response.data.data);

      yield put(actionsFactory.companyDetailsSuccess(
        normalize(company, companySchema)));
    } catch (error) {
      yield put(actionsFactory.companyDetailsError((error.data && error.data.error) || error));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return 'done';
  }

  function* getCompanyBusinessInfo(action) {
    try {
      const response =
        yield call(companiesService.getCompanyBusinessInfo, action.companyId, action.paramList);
      const pagination = mappingService.objectKeysToCamel(response.data && response.data.data);

      yield put(actionsFactory.reqBusinessInfoSuccess({
        items: normalize(pagination.items, businessInformationsSchema),
        total: pagination.totalItems,
        companyId: action.companyId,
      }));
      return response;
    } catch (err) {
      yield put(actionsFactory.reqBusinessInfoError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }
    return 'done';
  }

  function* getBusinessInfoOrder(action) {
    try {
      const response =
        yield call(businessInfoService.getBusinessInfoOrder, action.businessInfoId);
      const businessInformation = mappingService.objectKeysToCamel(
        response.data && response.data.data);

      businessInformation.content = utilities.getIndexedContent(businessInformation.content || []);
      businessInformation.notes = getIndexedNotes(businessInformation.notes || []);
      businessInformation.template = groupContiguousItemsType(businessInformation.template || {});

      const normalisedBI = normalize(businessInformation, businessInformationSchema);

      yield put(actionsFactory.reqBusinessInfoOrderSuccess(normalisedBI));
      return response;
    } catch (err) {
      yield put(actionsFactory.reqBusinessInfoOrderError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }
    return 'done';
  }

  function* getCompanyStructure(action) {
    try {
      const companyStructureRes =
        yield call(companiesService.getCompanyStructure, action.companyId);
      const companyStructure = companyStructureRes.data && companyStructureRes.data.data;
      const normalizedMasters = normalizeUsersByType('masters', companyStructure);
      const normalizedDirectors = normalizeUsersByType('directors', companyStructure);
      const normalizedBuyers = normalizeUsersByType('buyers', companyStructure);
      const normalizedBases = normalizeUsersByType('bases', companyStructure);

      const entities = R.reduce(R.mergeDeepWith((a, b) => {
        if (Array.isArray(a)) {
          return R.concat(a, b);
        }

        return b;
      }), {},
      [
        normalizedMasters.entities,
        normalizedDirectors.entities,
        normalizedBuyers.entities,
        normalizedBases.entities,
      ],
      );
      const normalizedStructure = Object.assign({}, companyStructure, {
        masters: normalizedMasters.result || [],
        buyers: normalizedBuyers.result || [],
        directors: normalizedDirectors.result || [],
        bases: normalizedBases.result || [],
        entities,
      });
      yield put(actionsFactory.reqCompanyStructureSuccess(normalizedStructure, action.companyId));
      return companyStructureRes;
    } catch (err) {
      yield put(actionsFactory.reqCompanyStructureError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }
    return 'done';
  }

  function* getTechDocList(action) {
    try {
      const response = yield call(companiesService.getTechDocList, action.companyId);
      const data = Object.assign({},
        normalize(mappingService.objectKeysToCamel(
          (response.data && response.data.data) || []), technicalInformationsSchema),
        { companyId: action.companyId });
      yield put(actionsFactory.reqTechDocListSuccess(data));
      return response;
    } catch (err) {
      yield put(actionsFactory.reqTechDocListError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return 'done';
  }

  function* getTechDocs(action) {
    try {
      const response =
        yield call(companiesService.getTechDoc, action.companyId, action.techDocId);
      const technicalInformation = mappingService.objectKeysToCamel(
        response.data && response.data.data);

      technicalInformation.content = utilities
        .getIndexedContent(technicalInformation.content || []);
      technicalInformation.template = groupContiguousItemsType(technicalInformation.template || {});
      const normalisedTI = normalize(technicalInformation, technicalInformationSchema);

      yield put(actionsFactory.reqTechDocSuccess({
        response: normalisedTI,
        companyId: action.companyId,
      }));
      return response;
    } catch (err) {
      yield put(actionsFactory.reqTechDocError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return 'done';
  }

  function* setTechDocToDraft(action) {
    try {
      const response = yield call(
        companiesService.setTechDocToDraft,
        action.companyId, action.techDocId);

      yield put(
        actionsFactory.setTechDocToDraftSuccess({ response }));

      $state.reload();

      return response;
    } catch (err) {
      yield put(actionsFactory.setTechDocToDraftError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return 'done';
  }

  function* updateTechDoc(action) {
    try {
      const mappedContent = getArrayContent(action.content);
      const response =
        yield call(companiesService.updateTechDoc,
          action.companyId, action.techDocId, mappedContent);
      const normalizedTechDoc = normalize(
        mappingService.objectKeysToCamel(response.data && response.data.data),
        technicalInformationSchema);
      const normalizedContent = mapContent(
        normalizedTechDoc.entities.technicalInformations[normalizedTechDoc.result].content);

      normalizedTechDoc.entities
        .technicalInformations[normalizedTechDoc.result].content = normalizedContent;

      yield put(actionsFactory.updateTechDocSuccess(normalizedTechDoc));
      return response;
    } catch (err) {
      yield put(actionsFactory.updateTechDocError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return 'done';
  }

  function* saveTechDoc(action) {
    try {
      const mappedContent = getArrayContent(action.content);
      yield call(companiesService.saveTechDoc, action.companyId, action.techDocId, mappedContent);
      yield put(actionsFactory.state());
    } catch (err) {
      yield put(actionsFactory.saveTechDocError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return 'done';
  }

  function* createTechDoc(action) {
    try {
      const response = yield call(companiesService.createTechDoc, action.companyId);
      const normalisedResponse = normalize(mappingService.objectKeysToCamel(
        (response.data && response.data.data) || null), technicalInformationSchema);
      if (normalisedResponse && normalisedResponse.entities.businessInformationTemplates) {
        normalisedResponse.entities.businessInformationTemplates =
          R.mapObjIndexed((template) => {
            const sections = template.sections.map(section => Object.assign({}, section, {
              groupedItems: R.groupWith((a, b) => a.type === b.type, section.items),
            }));

            return Object.assign({}, template, {
              sections,
            });
          }, normalisedResponse.entities.businessInformationTemplates);
      }

      yield put(actionsFactory.createTechDocSuccess({
        response: normalize(mappingService.objectKeysToCamel(
          (response.data && response.data.data) || null), technicalInformationSchema),
        companyId: action.companyId,
      }));
      return response;
    } catch (err) {
      yield put(actionsFactory.createTechDocError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return 'done';
  }

  function* updateBusinessInfoNotes(action) {
    try {
      const response =
        yield call(businessInfoService.updateBusinessInfoNotes,
          action.id,
          R.values(action.notes),
          action.score,
          action.trustStatement,
          action.companyContacts);

      const businessInformation = mappingService.objectKeysToCamel(
        response.data && response.data.data);

      businessInformation.content = utilities.getIndexedContent(businessInformation.content || []);
      businessInformation.notes = getIndexedNotes(businessInformation.notes || []);
      businessInformation.template = groupContiguousItemsType(businessInformation.template || {});

      const normalisedBI = normalize(businessInformation, businessInformationSchema);

      yield put(actionsFactory.updateBusinessInfoNotesSuccess(normalisedBI));
      // BI redirect on save
      // if (action.companyId) {
      //   $state.go('authenticated.company.detail.documents', { id: action.companyId });
      // }
      return response;
    } catch (err) {
      yield put(actionsFactory.updateBusinessInfoNotesError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return 'done';
  }

  function* createCompany(action) {
    try {
      const response = yield call(companiesService.createCompany, action.data);
      const normalizedResponse = normalize(
        mappingService.objectKeysToCamel(response.data.data),
        companySchema);
      yield put(actionsFactory.createCompanySuccess(normalizedResponse));
      if ($state.current.name === 'authenticated.orders') {
        $state.go('authenticated.orders');
      } else {
        $state.go('authenticated.company.detail.documents', { id: normalizedResponse.result });
      }
      return response;
    } catch (err) {
      yield put(actionsFactory.createCompanyError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return 'done';
  }

  function* updateBusinessInfoStatus(action) {
    try {
      // yield call(businessInfoService.updateBusinessInfoNotes, action.id,
      //   R.values(action.notes), action.score, action.trustStatement);
      const response =
        yield call(businessInfoService.setBusinessInfoCompleted,
          action.id,
          action.notes,
          action.score,
          action.trustStatement,
          action.companyContacts);

      const businessInformation = mappingService.objectKeysToCamel(
        response.data && response.data.data);

      businessInformation.content = utilities.getIndexedContent(businessInformation.content || []);
      businessInformation.notes = getIndexedNotes(businessInformation.notes || []);
      businessInformation.template = groupContiguousItemsType(businessInformation.template || {});

      const normalisedBI = normalize(businessInformation, businessInformationSchema);

      // const normalisedBI = normalize(mappingService.objectKeysToCamel(
      //   response.data && response.data.data), businessInformationSchema);

      yield put(actionsFactory
        .setBusinessInfoCompletedSuccess(normalisedBI));

      $state.go('authenticated.company.detail.documents', { id: action.companyId });

      return response;
    } catch (err) {
      yield put(actionsFactory.setBusinessInfoCompletedError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return 'done';
  }

  function* doSearchCompany(action) {
    try {
      const searchApi = companiesService[action.data.searchApi];
      const searchCompanyRes = yield call(searchApi, action.data);
      const searchCompany = searchCompanyRes.data && searchCompanyRes.data.data;
      yield put(actionsFactory.searchCompanySuccess(searchCompany));
      $rootScope.$broadcast('companies-search-ended');
      return searchCompanyRes;
    } catch (err) {
      yield put(actionsFactory.searchCompanyError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return 'done';
  }

  function* removeTechDoc(action) {
    try {
      const removeTechDocRes =
        yield call(companiesService.removeTechDoc, action.companyId, action.techDocId);
      const removeTechDocData = removeTechDocRes.data && removeTechDocRes.data.data;
      yield put(
        actionsFactory.removeTechDocSuccess(action.companyId, action.techDocId, removeTechDocData),
      );
      return removeTechDocData;
    } catch (err) {
      yield put(actionsFactory.removeTechDocError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return 'done';
  }

  function* getCompanyContracts(action) {
    try {
      const response =
        yield call(companiesService.getCompanyContracts, action.companyId);

      yield put(actionsFactory.reqCompanyContractsSuccess(
        normalize(mappingService.objectKeysToCamel(response.data && response.data.data),
          contractsSchema),
        action.companyId));
      return response;
    } catch (err) {
      yield put(actionsFactory.reqCompanyContractsError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }
    return 'done';
  }

  function* updateCompanyContract(action) {
    try {
      const response =
        yield call(companiesService.updateCompanyContract,
          action.companyId, action.contractId, action.contract);
      yield put(actionsFactory.updateContractSuccess(
        normalize(
          mappingService.objectKeysToCamel(response.data && response.data.data),
          contractSchema),
        action.companyId));
      return response;
    } catch (err) {
      yield put(actionsFactory.updateContractError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }
    return 'done';
  }

  function* createContract(action) {
    try {
      const response =
        yield call(companiesService.createContract, action.companyId, action.contract);

      yield put(actionsFactory.createContractSuccess(
        normalize(
          mappingService.objectKeysToCamel(response.data && response.data.data),
          contractSchema),
        action.companyId));
      return response;
    } catch (err) {
      yield put(actionsFactory.createContractError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }
    return action;
  }

  function* getCompanyBudgets(action) {
    const response =
      yield call(companiesService.getCompanyBudgets, action.companyId);

    const camelResponse = mappingService.objectKeysToCamel(response.data && response.data.data);

    const normalisedContracts = camelResponse.contract
      ? normalize(camelResponse.contract, contractSchema)
      : { entities: {} };
    const normalisedBudgets = normalize(camelResponse.budgets, budgetsSchema);

    const data = {
      entities: Object.assign({}, normalisedContracts.entities, normalisedBudgets.entities),
      result: normalisedBudgets.result,
    };

    yield put(actionsFactory.reqCompanyBudgetsSuccess(data, action.companyId));
  }

  function* createCompanyBudget(action) {
    try {
      const response =
        yield call(companiesService.createCompanyBudget,
          action.companyId, action.ownerId, action.parentBudgetId);
      yield put(actionsFactory.createBudgetSuccess(
        action.companyId,
        normalize(
          mappingService.objectKeysToCamel(response.data && response.data.data),
          budgetSchema)));
    } catch (err) {
      yield put(actionsFactory.createBudgetError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return action;
  }

  function* updateCompanyBudget(action) {
    try {
      const response =
        yield call(companiesService.updateCompanyBudget,
          action.companyId, action.budgetId, action.credits);
      yield put(actionsFactory.updateBudgetSuccess(
        normalize(
          mappingService.objectKeysToCamel(response.data && response.data.data),
          budgetSchema)));
    } catch (err) {
      yield put(actionsFactory.updateBudgetError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return action;
  }

  function* editCompany(action) {
    try {
      const response = yield call(companiesService.editCompany, action.id, action.company);
      yield put(actionsFactory.editCompanySuccess(
        normalize(
          mappingService.objectKeysToCamel(response.data && response.data.data),
          companySchema),
        action.id));
      return response;
    } catch (err) {
      yield put(actionsFactory.editCompanyError(err));
    } finally {
      if (yield cancelled()) {
        console.log('cancelled');
      }
    }

    return action;
  }


  function* companyListFlow() {
    yield takeLatest(REQUEST_COMPANYLIST_START, getCompanyList);
  }

  function* companyFlow() {
    yield takeLatest(REQUEST_COMPANY_START, getCompanyDetail);
    yield takeLatest(EDIT_COMPANY_START, editCompany);
  }

  function* businessInfoFlow() {
    yield takeLatest(REQUEST_BUSINESSINFO_START, getCompanyBusinessInfo);
  }

  function* businessInfoOrderFlow() {
    yield takeLatest(REQUEST_BUSINESSINFO_ORDER_START, getBusinessInfoOrder);
  }

  function* companyStructureFlow() {
    yield takeLatest(REQUEST_COMPANY_STRUCTURE_START, getCompanyStructure);
  }

  function* techDocsFlow() {
    yield takeLatest(REQUEST_TECHDOC_START, getTechDocs);
    yield takeLatest(SET_TECHDOC_TO_DRAFT_START, setTechDocToDraft);
  }

  function* techDocListFlow() {
    yield takeLatest(REQUEST_TECHDOCLIST_START, getTechDocList);
  }

  function* techDocUpdateFlow() {
    yield takeLatest(UPDATE_TECHDOC, updateTechDoc);
  }

  function* techDocSaveFlow() {
    yield takeLatest(SAVE_TECHDOC_START, saveTechDoc);
  }

  function* createTechDocFlow() {
    yield takeLatest(CREATE_TECHDOC, createTechDoc);
  }

  function* updateBusinessInfoNotesFlow() {
    yield takeLatest(UPDATE_BUSINESSINFO_NOTES_START, updateBusinessInfoNotes);
  }

  function* createCompanyFlow() {
    yield takeLatest(ADD_COMPANY, createCompany);
  }

  function* updateBusinessInfoStatusFlow() {
    yield takeLatest(UPDATE_BUSINESSINFO_STATUS_START, updateBusinessInfoStatus);
  }

  function* searchCompanyFlow() {
    yield takeLatest(SEARCH_COMPANY_START, doSearchCompany);
  }

  function* removeTechDocFlow() {
    yield takeLatest(REMOVE_TECHDOC_START, removeTechDoc);
  }

  function* companyBudgetsFlow() {
    yield takeLatest(REQUEST_COMPANY_BUDGETS_START, getCompanyBudgets);
    yield takeLatest(CREATE_COMPANY_BUDGET_START, createCompanyBudget);
    yield takeLatest(UPDATE_COMPANY_BUDGET_START, updateCompanyBudget);
  }

  function* companyContractsFlow() {
    yield takeLatest(REQUEST_COMPANY_CONTRACTS_START, getCompanyContracts);
    yield takeLatest(UPDATE_CONTRACT_START, updateCompanyContract);
    yield takeLatest(CREATE_CONTRACT_START, createContract);
  }

  function mapContent(content) {
    const allGroupData = R.filter(R.propEq('type', 'group'), content);
    const allGroupDataWithCorrectFields = R.map(utilities.withFieldsId, allGroupData);
    const allFieldsData = R.filter(R.propEq('type', 'field'), content);
    const result = {};
    result.fields = {};

    const filteredGroups = allGroupDataWithCorrectFields.map(group =>
      Object.assign({}, group, { fields: filterGroupContent(group.fields) }));

    result.groups = R.reduce(utilities.byId, {}, filteredGroups);
    result.fields = R.reduce(utilities.byId, {}, allFieldsData);
    return result;
  }


  function getArrayContent(content) {
    return R.concat(
      R.values(content.fields),
      R.map(group => Object.assign({}, R.omit('items', group), {
        items: R.map(item => getArrayContent(item), group.items || []),
      }),
      R.values(content.groups)));
  }

  function getIndexedNotes(notes, field = 'sectionId') {
    return R.indexBy(R.prop(field), notes);
  }

  // function getArrayNotes(notes) {
  //   return R.values(notes);
  // }

  function groupContiguousItemsType(template) {
    const sections = template
      .sections
      .map(section => Object.assign({}, section, {
        contiguousItemsWithSameType: getCountiguousItemsWithSameType(section),
      }));

    return Object.assign({}, template, {
      sections,
    });
  }

  function getCountiguousItemsWithSameType(el, key = 'items') {
    return R
      .groupWith((a, b) => a.type === b.type, el[key])
      .filter(adjs => adjs && adjs.length > 0)
      .map((c) => {
        const type = c[0].type;
        return {
          type,
          items: type === 'field'
            ? c
            : c.map(g => Object.assign({}, g,
              { contiguousItemsWithSameType: getCountiguousItemsWithSameType(g) })),
        };
      });
  }

  function filterGroupContent(rows) {
    const isColumnEmpty = element =>
      !element || R.isNil(element.value) || R.isEmpty(element.value) || element.value === 'NaN';

    return (rows || [])
      .filter(row =>
        !R.values(row)
          .map(column => isColumnEmpty(column))
          .reduce((columnEmpty, rowEmpty) => rowEmpty && columnEmpty));
  }

  function normalizeUsersByType(type, companyStructure) {
    return normalize(
      mappingService.objectKeysToCamel(companyStructure[type] || []),
      usersSchema,
    );
  }
}
